import { useEffect, useState } from 'react'
import { Plus, Trash2, Loader2, Sparkles } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Switch } from '../../lib/shadcn/switch'
import { toast } from '../../lib/shadcn/sonner'
import { useGetEmailVoice, useUpdateEmailVoice } from '../../hooks/backend/crm'

interface VoiceExample {
  subject: string
  body: string
}

interface VoiceSettings {
  style_guide: string
  use_history: boolean
  examples: VoiceExample[]
}

export default function EmailVoiceSettings() {
  const getVoice = useGetEmailVoice()
  const updateVoice = useUpdateEmailVoice()

  const [styleGuide, setStyleGuide] = useState('')
  const [useHistory, setUseHistory] = useState(true)
  const [examples, setExamples] = useState<VoiceExample[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void getVoice.trigger({}).then((data: unknown) => {
      const v = data as VoiceSettings | undefined
      if (v) {
        setStyleGuide(v.style_guide ?? '')
        setUseHistory(v.use_history ?? true)
        setExamples(Array.isArray(v.examples) ? v.examples : [])
      }
      setLoaded(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addExample = () => setExamples(prev => [...prev, { subject: '', body: '' }])
  const removeExample = (idx: number) => setExamples(prev => prev.filter((_, i) => i !== idx))
  const updateExample = (idx: number, key: keyof VoiceExample, value: string) =>
    setExamples(prev => prev.map((e, i) => (i === idx ? { ...e, [key]: value } : e)))

  const handleSave = async () => {
    try {
      await updateVoice.trigger({
        style_guide: styleGuide,
        use_history: useHistory,
        examples: examples.filter(e => e.body.trim()),
      })
      toast.success('Email voice saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          AI Email Voice
        </CardTitle>
        <CardDescription>
          Teach the AI to write in your voice. Used whenever you click &quot;Write with AI&quot; on an email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!loaded ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            {/* Style guide */}
            <div className="space-y-1.5">
              <Label htmlFor="voice-style">Style guide</Label>
              <p className="text-xs text-muted-foreground">
                Describe your tone, greetings, sign-off, phrases you use or avoid, and formality.
              </p>
              <Textarea
                id="voice-style"
                value={styleGuide}
                onChange={e => setStyleGuide(e.target.value)}
                rows={5}
                placeholder={'e.g. Warm and casual. Open with "Hey [name]!" Keep it short. Sign off "Cheers, Sam". Avoid corporate jargon. Use a little West Texas charm.'}
              />
            </div>

            {/* Learn from history */}
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="voice-history" className="cursor-pointer">Learn from my recent sent emails</Label>
                <p className="text-xs text-muted-foreground">
                  Uses your latest sent emails as examples so drafts sound like you.
                </p>
              </div>
              <Switch id="voice-history" checked={useHistory} onCheckedChange={setUseHistory} />
            </div>

            {/* Manual example emails */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Example emails</Label>
                <Button type="button" variant="outline" size="sm" className="gap-1.5 h-7" onClick={addExample}>
                  <Plus className="h-3.5 w-3.5" /> Add example
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Paste a few emails that sound like you. These carry the most weight for matching your voice.
              </p>
              {examples.length === 0 ? (
                <p className="text-sm text-muted-foreground py-1">No example emails yet.</p>
              ) : (
                <div className="space-y-3">
                  {examples.map((ex, idx) => (
                    <div key={idx} className="space-y-2 rounded-md border p-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={ex.subject}
                          onChange={e => updateExample(idx, 'subject', e.target.value)}
                          placeholder="Subject (optional)"
                          className="h-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-muted-foreground hover:text-destructive shrink-0"
                          aria-label="Remove example"
                          onClick={() => removeExample(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Textarea
                        value={ex.body}
                        onChange={e => updateExample(idx, 'body', e.target.value)}
                        rows={4}
                        placeholder="Paste the email text here…"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={() => void handleSave()} disabled={updateVoice.loading} className="gap-1.5">
                {updateVoice.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Voice
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
